import {
  PDF_DEFAULT_MARGINS,
  type DocumentDefinition,
  type DocumentKind,
  type DocumentPageRef,
  type PdfMargins,
  type PdfPageSizeKey,
} from './types';
import { REPORT_SAMPLE_VARS } from './sample-data';
import { buildFlowTextLayer, getFlowTextPresetForKind } from './flowtext-presets';
import { buildPolishedPdfTemplateFromComposerPreset } from '@/editor/utils/polishedPdfTemplates';

export { REPORT_SAMPLE_VARS, mergeWithSampleDefaults } from './sample-data';
export { buildFlowTextLayer, getFlowTextPresetForKind, getFlowTextPresets } from './flowtext-presets';

export interface PdfDocumentTemplatePreset {
  id: string;
  doc_kind: Exclude<DocumentKind, 'custom'>;
  title: string;
  description: string;
  page_size: PdfPageSizeKey;
  margins: PdfMargins;
  pages: Array<Omit<DocumentPageRef, 'id' | 'sort_order'>>;
}

const DEFAULT_RENDER_WIDTH = 1200;
const DEFAULT_RENDER_HEIGHT = 2400;

type PresetPageSeed = {
  slug: string;
  label: string;
  variables?: Record<string, string>;
  render_height?: number;
};

const reportPages: PresetPageSeed[] = [
  { slug: 't8zpms', label: 'Cover', variables: { ...REPORT_SAMPLE_VARS, section_title: 'Executive Summary', page_number: '1', page_total: '6' } },
  { slug: 't8zpms-vwxi', label: 'KPI Summary', variables: { ...REPORT_SAMPLE_VARS, section_title: 'KPI Snapshot', page_number: '2', page_total: '6' } },
  { slug: '2z6rak-s33r', label: 'Traffic Trends', variables: { ...REPORT_SAMPLE_VARS, section_title: 'Traffic Trends', page_number: '3', page_total: '6' } },
  { slug: 't8zpms-vwxi', label: 'Conversion Funnel', variables: { ...REPORT_SAMPLE_VARS, section_title: 'Conversion Performance', page_number: '4', page_total: '6' } },
  { slug: '2z6rak-s33r', label: 'Keyword & Visibility', variables: { ...REPORT_SAMPLE_VARS, section_title: 'Organic Visibility', page_number: '5', page_total: '6' } },
  { slug: 't8zpms', label: 'Recommendations', variables: { ...REPORT_SAMPLE_VARS, section_title: 'Next-Step Plan', page_number: '6', page_total: '6' } },
];

const proposalPages: PresetPageSeed[] = [
  { slug: 't8zpms', label: 'Cover Letter', variables: { section_title: 'Client Proposal', page_number: '1', page_total: '5' } },
  { slug: '2z6rak-s33r', label: 'Current State', variables: { section_title: 'Current State Audit', page_number: '2', page_total: '5' } },
  { slug: 't8zpms-vwxi', label: 'Strategy', variables: { section_title: 'Growth Strategy', page_number: '3', page_total: '5' } },
  { slug: '2z6rak-s33r', label: 'Timeline & Deliverables', variables: { section_title: 'Roadmap & Deliverables', page_number: '4', page_total: '5' } },
  { slug: 't8zpms', label: 'Investment & Next Steps', variables: { section_title: 'Investment & Approval', page_number: '5', page_total: '5' } },
];

const contractPages: PresetPageSeed[] = [
  { slug: '2z6rak-s33r', label: 'Agreement Summary', variables: { section_title: 'Service Agreement', page_number: '1', page_total: '4' } },
  { slug: 't8zpms-vwxi', label: 'Scope & Terms', variables: { section_title: 'Scope & Terms', page_number: '2', page_total: '4' } },
  { slug: 't8zpms', label: 'Payment & SLAs', variables: { section_title: 'Fees & SLAs', page_number: '3', page_total: '4' } },
  { slug: 't8zpms-vwxi', label: 'Signatures', variables: { section_title: 'Acceptance & Signatures', page_number: '4', page_total: '4' } },
];

const invoicePages: PresetPageSeed[] = [
  { slug: 't8zpms-vwxi', label: 'Invoice Summary', variables: { section_title: 'Invoice Statement', page_number: '1', page_total: '2' } },
  { slug: 't8zpms', label: 'Line Items & Notes', variables: { section_title: 'Line Items', page_number: '2', page_total: '2' } },
];

const ndaPages: PresetPageSeed[] = [
  { slug: '2z6rak-s33r', label: 'Terms & Definitions', variables: { section_title: 'Non-Disclosure Agreement', page_number: '1', page_total: '3' } },
  { slug: 't8zpms-vwxi', label: 'Obligations & Exclusions', variables: { section_title: 'Obligations', page_number: '2', page_total: '3' } },
  { slug: 't8zpms', label: 'Signatures', variables: { section_title: 'Execution', page_number: '3', page_total: '3' } },
];

const freelanceContractPages: PresetPageSeed[] = [
  { slug: 't8zpms', label: 'Cover & Parties', variables: { section_title: 'Freelance Contract', page_number: '1', page_total: '4' } },
  { slug: '2z6rak-s33r', label: 'Scope & Timeline', variables: { section_title: 'Project Scope', page_number: '2', page_total: '4' } },
  { slug: 't8zpms-vwxi', label: 'Compensation & IP', variables: { section_title: 'Terms & Compensation', page_number: '3', page_total: '4' } },
  { slug: 't8zpms', label: 'Signatures', variables: { section_title: 'Acceptance', page_number: '4', page_total: '4' } },
];

const employmentAgreementPages: PresetPageSeed[] = [
  { slug: 't8zpms', label: 'Cover & Parties', variables: { section_title: 'Employment Agreement', page_number: '1', page_total: '5' } },
  { slug: '2z6rak-s33r', label: 'Position & Duties', variables: { section_title: 'Role & Responsibilities', page_number: '2', page_total: '5' } },
  { slug: 't8zpms-vwxi', label: 'Compensation & Benefits', variables: { section_title: 'Compensation Package', page_number: '3', page_total: '5' } },
  { slug: '2z6rak-s33r', label: 'Confidentiality & IP', variables: { section_title: 'Confidentiality', page_number: '4', page_total: '5' } },
  { slug: 't8zpms', label: 'Signatures', variables: { section_title: 'Execution', page_number: '5', page_total: '5' } },
];

const scopeOfWorkPages: PresetPageSeed[] = [
  { slug: 't8zpms', label: 'Overview & Objectives', variables: { section_title: 'Scope of Work', page_number: '1', page_total: '4' } },
  { slug: '2z6rak-s33r', label: 'Deliverables & Timeline', variables: { section_title: 'Deliverables', page_number: '2', page_total: '4' } },
  { slug: 't8zpms-vwxi', label: 'Responsibilities', variables: { section_title: 'Roles & Responsibilities', page_number: '3', page_total: '4' } },
  { slug: 't8zpms', label: 'Acceptance & Sign-off', variables: { section_title: 'Acceptance', page_number: '4', page_total: '4' } },
];

const quotePages: PresetPageSeed[] = [
  { slug: 't8zpms-vwxi', label: 'Quote Summary', variables: { section_title: 'Project Quote', page_number: '1', page_total: '2' } },
  { slug: 't8zpms', label: 'Terms & Acceptance', variables: { section_title: 'Terms', page_number: '2', page_total: '2' } },
];

const letterOfIntentPages: PresetPageSeed[] = [
  { slug: 't8zpms', label: 'Purpose & Proposed Terms', variables: { section_title: 'Letter of Intent', page_number: '1', page_total: '3' } },
  { slug: '2z6rak-s33r', label: 'Due Diligence & Exclusivity', variables: { section_title: 'Due Diligence', page_number: '2', page_total: '3' } },
  { slug: 't8zpms-vwxi', label: 'Acknowledgment', variables: { section_title: 'Execution', page_number: '3', page_total: '3' } },
];

const toPages = (pages: PresetPageSeed[]): Array<Omit<DocumentPageRef, 'id' | 'sort_order'>> =>
  pages.map((page, index) => ({
    slug: page.slug,
    label: page.label || `Page ${index + 1}`,
    render_format: 'png',
    render_width: DEFAULT_RENDER_WIDTH,
    render_height: page.render_height || DEFAULT_RENDER_HEIGHT,
    variables: page.variables || {},
  }));

export const PDF_DOCUMENT_TEMPLATE_PRESETS: PdfDocumentTemplatePreset[] = [
  {
    id: 'report-monthly-performance',
    doc_kind: 'report',
    title: 'Monthly Performance Report',
    description: 'Executive summary, KPI snapshots, trend analysis, and recommendation pages for recurring client reporting.',
    page_size: 'letter',
    margins: PDF_DEFAULT_MARGINS,
    pages: toPages(reportPages),
  },
  {
    id: 'proposal-seo-growth',
    doc_kind: 'proposal',
    title: 'SEO Growth Proposal',
    description: 'Five-page proposal framework with discovery, strategy, timeline, and investment sections.',
    page_size: 'letter',
    margins: PDF_DEFAULT_MARGINS,
    pages: toPages(proposalPages),
  },
  {
    id: 'contract-retainer-basic',
    doc_kind: 'contract',
    title: 'Marketing Retainer Contract',
    description: 'Agreement-ready contract pages covering scope, terms, fees, and signatures.',
    page_size: 'letter',
    margins: PDF_DEFAULT_MARGINS,
    pages: toPages(contractPages),
  },
  {
    id: 'invoice-service-statement',
    doc_kind: 'invoice',
    title: 'Service Invoice Statement',
    description: 'Two-page invoice packet with summary page and detailed line-item follow-up.',
    page_size: 'letter',
    margins: PDF_DEFAULT_MARGINS,
    pages: toPages(invoicePages),
  },
  {
    id: 'nda-standard',
    doc_kind: 'nda',
    title: 'Standard NDA',
    description: 'Three-page non-disclosure agreement covering confidential information definitions, obligations, and execution.',
    page_size: 'letter',
    margins: PDF_DEFAULT_MARGINS,
    pages: toPages(ndaPages),
  },
  {
    id: 'freelance-contract',
    doc_kind: 'freelance_contract',
    title: 'Freelance Service Contract',
    description: 'Four-page freelance contract with project scope, timeline, compensation terms, and IP assignment.',
    page_size: 'letter',
    margins: PDF_DEFAULT_MARGINS,
    pages: toPages(freelanceContractPages),
  },
  {
    id: 'employment-agreement',
    doc_kind: 'employment_agreement',
    title: 'Employment Agreement',
    description: 'Five-page employment agreement covering position, compensation, benefits, confidentiality, and non-compete.',
    page_size: 'letter',
    margins: PDF_DEFAULT_MARGINS,
    pages: toPages(employmentAgreementPages),
  },
  {
    id: 'scope-of-work',
    doc_kind: 'scope_of_work',
    title: 'Scope of Work',
    description: 'Four-page SOW defining project objectives, deliverables, timeline, responsibilities, and acceptance criteria.',
    page_size: 'letter',
    margins: PDF_DEFAULT_MARGINS,
    pages: toPages(scopeOfWorkPages),
  },
  {
    id: 'quote-estimate',
    doc_kind: 'quote',
    title: 'Project Quote & Estimate',
    description: 'Two-page project quote with line-item pricing, notes, payment terms, and client acceptance.',
    page_size: 'letter',
    margins: PDF_DEFAULT_MARGINS,
    pages: toPages(quotePages),
  },
  {
    id: 'letter-of-intent',
    doc_kind: 'letter_of_intent',
    title: 'Letter of Intent',
    description: 'Three-page LOI outlining proposed transaction terms, due diligence period, exclusivity, and acknowledgment.',
    page_size: 'letter',
    margins: PDF_DEFAULT_MARGINS,
    pages: toPages(letterOfIntentPages),
  },
];

export function getPdfTemplatePreset(templateId: string): PdfDocumentTemplatePreset | null {
  return PDF_DOCUMENT_TEMPLATE_PRESETS.find((template) => template.id === templateId) || null;
}

export function createDefinitionFromPdfTemplate(
  template: PdfDocumentTemplatePreset,
  overrides?: {
    title?: string;
    page_size?: PdfPageSizeKey;
    margins?: PdfMargins;
    pageVariables?: Array<Record<string, string>>;
    includeFlowText?: boolean;
  },
): DocumentDefinition {
  const polished = buildPolishedPdfTemplateFromComposerPreset(template.id);
  const pages = polished
    ? polished.pages.map((page, index) => ({
      id: page.id,
      slug: template.pages[index]?.slug || '',
      label: page.label || template.pages[index]?.label || `Page ${index + 1}`,
      width: page.width,
      height: page.height,
      render_format: 'png' as const,
      render_width: page.width,
      render_height: page.height,
      layers: polished.layersByPageId[page.id] || [],
      variables: overrides?.pageVariables?.[index] || template.pages[index]?.variables || {},
      template_id: template.id,
      sort_order: index,
    }))
    : template.pages.map((page, index) => ({
      ...page,
      id: crypto.randomUUID(),
      sort_order: index,
      variables: overrides?.pageVariables?.[index] || page.variables || {},
    }));

  const def: DocumentDefinition = {
    schema_version: 1,
    doc_kind: template.doc_kind,
    title: overrides?.title?.trim() || template.title,
    description: template.description,
    page_size: overrides?.page_size || template.page_size,
    margins: overrides?.margins || template.margins,
    pages,
    metadata: { source_preset_id: template.id },
  };

  if (polished && polished.documentLayers.length > 0) {
    def.document_layers = polished.documentLayers;
  }

  // Auto-include FlowText layer matching the doc kind unless explicitly disabled.
  // Polished packs author their pages as structured layers (tables, cards,
  // signature blocks); overlaying a generic flow body on top of them would
  // collide, so the fallback only applies to slug-based legacy presets.
  const includeFlow = overrides?.includeFlowText !== false && !polished;
  if (includeFlow && !def.document_layers?.length) {
    const presetId = getFlowTextPresetForKind(template.doc_kind);
    if (presetId) {
      def.document_layers = [buildFlowTextLayer(presetId)];
    }
  }

  return def;
}
